import React, { useState, useEffect, useMemo, useRef } from "react";
import { useQuery, useMutation, useLazyQuery } from "@apollo/client";
import "../../../index.css";
import { format, isToday, parse, isAfter } from "date-fns";
import ApartmentSearch from "./apartmentSearch";
import BackButton from "../../CommonComponents/BackButton";
import LabledInput from "../../CommonComponents/LabledInput";
import CustomSelect from "../../CommonComponents/CustomSelect";
import {
  GET_AVAILABLE_EMPLOYEE,
  GET_AVAILABLE_SLOTS,
  CREATE_USER,
  GET_USER_BY_PHONE,
  GET_SERVICES,
  CREATE_BOOKING,
  EMPLOYEE_LIST,
  UPDATE_USER,
  GET_PREREQUISITE_CHECKLIST_ITEMS,
} from "../graphql";
import { se } from "date-fns/locale";
import { remoteConfig } from "../../../configfirebase";

/* -------------------- Utils -------------------- */
const DEFAULT_COD_CHARGES_MAP = {};

const generateNext7Days = () => {
  const today = new Date();
  return Array.from({ length: 7 }, (_, i) => {
    const date = new Date(today);
    date.setDate(today.getDate() + i);
    return {
      value: format(date, "yyyy-MM-dd"), // For query
      label: format(date, "dd EEE"), // Display: 28 SAT
    };
  });
};

/* -------------------- Component -------------------- */
const CreateBooking = ({ setCreateBookingForm }) => {
  /* ---------- User ---------- */
  const [phone, setPhone] = useState("");
  const [customer, setCustomer] = useState(null);
  const [name, setName] = useState("");
  const [gender, setGender] = useState("");
  const [dob, setDob] = useState("");
  const [editableName, setEditableName] = useState("");
  const [isSavingName, setIsSavingName] = useState(false);

  /* ---------- Address ---------- */
  const [addresses, setAddresses] = useState([]);
  const [selectedAddressIndex, setSelectedAddressIndex] = useState("");
  const [isManualAddress, setIsManualAddress] = useState(false);
  const [isLocationChecked, setIsLocationChecked] = useState(false);

  const [flatNo, setFlatNo] = useState("");
  const [selectedApartment, setSelectedApartment] = useState("");
  const [slotEmployeeIds, setSlotEmployeeIds] = useState([]);

  /* ---------- Services ---------- */
  const [selectedServices, setSelectedServices] = useState([]);
  const [serviceList, setServiceList] = useState([]);
  const [serviceSearchTerm, setServiceSearchTerm] = useState("");
  const [filteredServices, setFilteredServices] = useState([]);
  const [allServices, setAllServices] = useState([]);

  /* ---------- Prerequisite checklist ---------- */
  const [prerequisiteCheckEnabled, setPrerequisiteCheckEnabled] =
    useState(false);
  const [applicableChecklistItems, setApplicableChecklistItems] = useState(
    []
  );
  const [acceptedChecklistItemIds, setAcceptedChecklistItemIds] = useState(
    []
  );

  /* ---------- Slots ---------- */
  const [date, setDate] = useState("");
  const [selectedSlot, setSelectedSlot] = useState("");
  const [slots, setSlots] = useState([]);

  /* ---------- Employees ---------- */
  const [skipSlotValidation, setSkipSlotValidation] = useState(false);
  const [allEmployees, setAllEmployees] = useState([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("");

  /* ---------- Booking ---------- */
  const [codChargesMap, setCodChargesMap] = useState(DEFAULT_COD_CHARGES_MAP);
  const [codCharges, setCodCharges] = useState("");
  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingError, setBookingError] = useState(null);
  const isSubmittingBookingRef = useRef(false);

  /* -------------------- Queries -------------------- */

  const [createUser, { loading: creatingUser, error: createUserError }] =
    useMutation(CREATE_USER);
  const { data: userData, refetch: searchUser } = useQuery(GET_USER_BY_PHONE, {
    variables: { contactNumber: phone },
    skip: phone.length !== 10,
  });

  const { data: servicesData, refetch: refetchServices } = useQuery(
    GET_SERVICES,
    {
      variables: { data: { gender: customer?.gender, order: "", search: "" } },
      skip: !customer?.gender,
    }
  );

  const [
    getAvailableSlots,
    { data: availableSlotsData, loading: slotsLoading },
  ] = useLazyQuery(GET_AVAILABLE_SLOTS);

  const [
    getAvailableEmployee,
    { data: availableEmpData, loading: empLoading },
  ] = useLazyQuery(GET_AVAILABLE_EMPLOYEE);

  const [getEmployees, { data: employeesData }] = useLazyQuery(EMPLOYEE_LIST, {
    variables: { data: { bookableOrEmployed: true } },
  });

  const [createBooking] = useMutation(CREATE_BOOKING);
  const [updateUserName] = useMutation(UPDATE_USER);
  const [getPrerequisiteChecklistItems] = useLazyQuery(
    GET_PREREQUISITE_CHECKLIST_ITEMS
  );

  /* -------------------- AUTO USER SEARCH -------------------- */
  useEffect(() => {
    if (phone.length === 10) {
      searchUser();
    }
  }, [phone, searchUser]);

  /* -------------------- REMOTE CONFIG -------------------- */
  useEffect(() => {
    console.log("[RemoteConfig] Starting fetchAndActivate...");
    remoteConfig
      .fetchAndActivate()
      .then((activated) => {
        console.log("[RemoteConfig] fetchAndActivate done. activated:", activated);
        const rawCod = remoteConfig.getValue("cod_charges").asString();
        console.log("[RemoteConfig] cod_charges raw value:", rawCod);
        if (rawCod) {
          try {
            const parsed = JSON.parse(rawCod);
            console.log("[RemoteConfig] cod_charges parsed:", parsed);
            setCodChargesMap(parsed);
          } catch (e) {
            console.error("[RemoteConfig] Error parsing cod_charges", e);
          }
        } else {
          console.warn("[RemoteConfig] cod_charges is empty — key may not exist in Firebase Remote Config");
        }
        setPrerequisiteCheckEnabled(
          remoteConfig.getValue("service_prerequisite_check_enabled").asBoolean()
        );
      })
      .catch((err) => console.error("[RemoteConfig] fetchAndActivate failed:", err));
  }, []);

  /* -------------------- PREREQUISITE CHECKLIST -------------------- */
  useEffect(() => {
    if (!prerequisiteCheckEnabled || selectedServices.length === 0) {
      setApplicableChecklistItems([]);
      setAcceptedChecklistItemIds([]);
      return;
    }
    getPrerequisiteChecklistItems({
      variables: { serviceIds: selectedServices.map((s) => s.id) },
    }).then((res) => {
      setApplicableChecklistItems(
        res.data?.getPrerequisiteChecklistItemsForServices || []
      );
      // Service set changed - force re-confirmation.
      setAcceptedChecklistItemIds([]);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prerequisiteCheckEnabled, selectedServices]);

  /* -------------------- USER RESULT -------------------- */
  useEffect(() => {
    if (customer) {
      setEditableName(customer.fullName || "");
    }
  }, [customer]);

  useEffect(() => {
    if (phone.length !== 10) {
      setCustomer(null);
      setAddresses([]);
      return;
    } else if (userData?.getUserlist?.user?.length) {
      const user = userData.getUserlist.user[0];

      setCustomer(user);
      setAddresses(user.address || []);
      setSelectedAddressIndex("");
      setFlatNo("");
      setSelectedApartment("");
      setIsManualAddress(false);
      setIsLocationChecked(false);
      refetchServices();
    } else if (phone.length === 10) {
      setCustomer(null);
      setAddresses([]);
    }
  }, [userData, phone, refetchServices]);

  /* -------------------- SERVICES -------------------- */
  useEffect(() => {
    if (servicesData?.getAdminService?.services) {
      setServiceList(servicesData.getAdminService.services);
    }
  }, [servicesData]);

  useEffect(() => {
    setFilteredServices(
      serviceList.filter((s) =>
        s.name.toLowerCase().includes(serviceSearchTerm.toLowerCase())
      )
    );
  }, [serviceSearchTerm, serviceList]);

  /* -------------------- SLOTS -------------------- */
  useEffect(() => {
    if (availableSlotsData?.getAvailableSlots) {
      setSlots(availableSlotsData.getAvailableSlots.slots);
    }
  }, [availableSlotsData]);

  useEffect(() => {
    if (date) {
      const dayName = format(new Date(date), "EEEE");
      setCodCharges(codChargesMap[dayName] || "");
    } else {
      setCodCharges("");
    }
  }, [date, codChargesMap]);

  /* -------------------- EMPLOYEES -------------------- */
  useEffect(() => {
    if (employeesData?.getEmployeelist?.employee) {
      setAllEmployees(employeesData.getEmployeelist.employee);
    }
  }, [employeesData]);

  /* -------------------- LOCATION CHECK RESULT -------------------- */
  useEffect(() => {
    if (availableEmpData) {
      setIsLocationChecked(true);
    }
  }, [availableEmpData]);

  /* -------------------- Handlers -------------------- */
  const handleSaveName = async () => {
    if (!editableName || editableName.trim().length === 0) return;

    try {
      setIsSavingName(true);

      const res = await updateUserName({
        variables: {
          data: {
            contactNumber: phone,
            fullName: editableName.trim(),
          },
        },
      });

      if (res.data.updateCustomer.status === "SUCCESS") {
        setCustomer((prev) => ({
          ...prev,
          fullName: editableName.trim(),
        }));
      } else {
        alert("Failed to save name");
      }
    } catch (err) {
      console.error(err);
      alert("Error saving name");
    } finally {
      setIsSavingName(false);
    }
  };

  const handleUpdateAddress = async () => {
    if (!customer) return;


    try {
      // Only keep flatNo and apartment keys for each address
      const updatedAddresses = [
        ...((addresses || []).map(addr => ({
          flatNo: addr.flatNo,
          apartment: addr.apartment,
        }))),
        {
          flatNo: flatNo,
          apartment: selectedApartment,
        },
      ];

      const res = await updateUserName({
        variables: {
          data: {
            contactNumber: phone,
            address: updatedAddresses,
          },
        },
      });

      if (res?.data?.updateCustomer?.status === "SUCCESS") {
        alert("Address updated successfully");

        // refresh user again (same like getUsers())
      } else {
        alert("Failed to update address");
      }
    } catch (err) {
      console.error(err);
      alert("Error updating address");
    }
  };

  const handleCreateUser = async () => {
    if (!name || !gender) {
      alert("Please provide both name and gender.");
      return;
    }

    try {
      const response = await createUser({
        variables: {
          data: {
            fullName: name,
            contactNumber: phone,
            gender,
            // dateOfBirth: dob ? new Date(dob).toISOString() : null,
          },
        },
        fetchPolicy: "no-cache",
      });

      const result = response?.data?.createuser;
      if (result?.status === "success" || result?.status === "SUCCESS") {
        alert("User created successfully.");
        searchUser();
      } else {
        alert(result?.message || "Failed to create user.");
      }
    } catch (err) {
      console.error("Create user error:", err);
      alert("Something went wrong.");
    }
  };

  const handleAddressSelect = (e) => {
    const index = e.target.value;
    setSelectedAddressIndex(index);

    if (index === "") return;

    const addr = addresses[Number(index)];
    if (!addr) return;

    setFlatNo(addr.flatNo);
    handleApartmentSelected(addr.apartment);
  };

  const handleDateSelect = (selectedDate) => {
    setDate(selectedDate);
    setSelectedSlot("");

    const totalTime = selectedServices.reduce(
      (sum, s) => sum + s.time * s.count,
      0
    );

    getAvailableSlots({
      variables: {
        data: {
          date: selectedDate,
          address: selectedApartment,
          femaleTimeSlot: customer?.gender === "Female",
          serviceTime: totalTime.toString(),
          skipSlotValidation,
        },
      },
      fetchPolicy: "network-only",
    });
  };

  const handleApartmentSelected = async (place) => {
    try {
      setSelectedApartment(place);
      setSelectedServices([]);
      setDate("");
      setSelectedSlot("");
      getAvailableEmployee({
        variables: {
          data: {
            address: place,
          },
        },
        fetchPolicy: "network-only",
      });

      // If admin toggles skip mode, fetch employees for selection
      if (skipSlotValidation) {
        // call the employee list API - adjust variables if your EMPLOYEE_LIST expects any
        getEmployees({ fetchPolicy: "network-only" });
      }
    } catch (error) {
      console.error("Error fetching location details:", error);
    }
  };

  const handleServiceSelect = (service) => {
    setSelectedServices((prev) => {
      const exists = prev.find((s) => s.id === service.id);
      if (exists) {
        return prev.map((s) =>
          s.id === service.id ? { ...s, count: s.count + 1 } : s
        );
      } else {
        return [...prev, { ...service, count: 1 }];
      }
    });
    setDate("");
    setSelectedSlot("");
  };

  const handleServiceRemove = (id) => {
    setSelectedServices((prev) => {
      return prev
        .map((s) => (s.id === id ? { ...s, count: s.count - 1 } : s))
        .filter((s) => s.count > 0);
    });
    setDate("");
    setSelectedSlot("");
  };

  const calculateTotal = () =>
    selectedServices.reduce((sum, s) => sum + s.price * s.count, 0);

  const filteredSlots = useMemo(() => {
    if (!date) return slots;
    if (isToday(new Date(date))) {
      const now = new Date();
      return slots.filter((slot) =>
        isAfter(parse(slot, "HH:mm", new Date()), now)
      );
    }
    return slots;
  }, [slots, date]);

  const isServicable =
    isLocationChecked && availableEmpData?.getAvailableEmployee?.employees > 0;
  const handleGenderChange = (e) => {
    const updatedGender = e.target.value;

    setCustomer((prev) => ({
      ...prev,
      gender: updatedGender,
    }));

    setSelectedServices([]);
    setDate("");
    setSelectedSlot("");

    refetchServices({ gender: updatedGender });
  };
  const handleToggleSkip = () => {
    setSkipSlotValidation((prev) => {
      const next = !prev;
      // when enabling skip, clear slot & fetch employees
      if (next) {
        setSelectedSlot("");
        setSlots([]);
        setSlotEmployeeIds([]);
        setSelectedEmployeeId("");
        getEmployees({ fetchPolicy: "network-only" });
      } else {
        // when disabling skip, clear selected employee
        setSelectedEmployeeId("");
      }
      return next;
    });
  };
  /* -------------------- Submit -------------------- */
  const handleSubmitBooking = async () => {
    if (isSubmittingBookingRef.current || bookingLoading) return;

    // validation: when skipping slot validation, selectedEmployeeId becomes mandatory
    if (!selectedApartment || !date || selectedServices.length === 0) {
      alert("Please complete all required fields");
      return;
    }
    if (!selectedSlot) {
      alert("Please provide a time slot.");
      return;
    }
    if (skipSlotValidation && !selectedEmployeeId) {
      alert(
        "Please select an employee (required when skipping slot validation)."
      );
      return;
    }
    if (
      prerequisiteCheckEnabled &&
      applicableChecklistItems.some(
        (item) => !acceptedChecklistItemIds.includes(item.id)
      )
    ) {
      alert("Please confirm all prerequisite checklist items before proceeding.");
      return;
    }

    isSubmittingBookingRef.current = true;
    setBookingLoading(true);
    setBookingError(null);

    const expandedServiceList = selectedServices.flatMap((s) =>
      Array.from({ length: s.count }).map(() => ({
        id: s.id,
        name: s.name,
        price: s.price,
        type: "basic",
      }))
    );

    const finalDataObj = {
      price: calculateTotal(),
      address: `${flatNo}, ${selectedApartment}`,
      paymentType: "COD",
      bookingDate: date,
      timeSlot: selectedSlot,
      couponCode: null,
      serviceList: expandedServiceList,
      lat: "",
      long: "",
      femaleTimeSlot: customer?.gender === "Female",
      employeeIds: skipSlotValidation
        ? selectedEmployeeId
          ? [selectedEmployeeId]
          : []
        : availableSlotsData?.getAvailableSlots?.employeeIds ?? [],
      couponDiscount: 0,
      contactNumber: phone,
      bookingType: "admin",
      codCharges: codCharges !== "" ? parseInt(codCharges, 10) : 0,
      skipSlotValidation, // optional flag for backend
      acceptedPrerequisiteChecklistItemIds: acceptedChecklistItemIds,
    };

    try {
      const res = await createBooking({
        variables: { data: finalDataObj },
      });
      if (res.data.createbooking.status === "SUCCESS") {
        alert("Booking created!");
        window.location.reload();
      } else {
        setBookingError(
          res.data.createbooking.message || "Failed to create booking."
        );
      }
    } catch (error) {
      console.error("Booking error:", error);
      setBookingError("Failed to create booking. Please try again.");
    } finally {
      setBookingLoading(false);
      isSubmittingBookingRef.current = false;
    }
  };

  /* -------------------- UI -------------------- */
  return (
    <div className="flex flex-col space-y-6">
      <BackButton onClick={() => setCreateBookingForm(false)} />
      <h3 className="text-[#FBB033] font-bold text-2xl">Create Booking</h3>

      {/* PHONE (ALWAYS) */}
      <LabledInput
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        label="Contact"
        placeholder="Enter phone number"
      />

      {/* USER FOUND */}
      {customer && (
        <div className="flex flex-col sm:flex-row sm:items-end gap-3">
          <LabledInput
            value={editableName}
            label="Name"
            onChange={(e) => setEditableName(e.target.value)}
            disabled={customer.fullName && customer.fullName.length > 0}
          />

          {/* SHOW SAVE ONLY WHEN NAME IS EMPTY INITIALLY */}
          {!customer.fullName && editableName.trim().length > 0 && (
            <button
              onClick={handleSaveName}
              disabled={isSavingName}
              className="px-4 py-2 bg-[#FBB034] rounded font-bold"
            >
              {isSavingName ? "Saving..." : "Save"}
            </button>
          )}
        </div>
      )}

      {/* USER NOT FOUND - CREATE */}
      {!customer && phone.length === 10 && (
        <div>
          <p className="text-white mb-2">
            No user found with this phone number. Please create a user first.
          </p>

          <div className="flex flex-col gap-3">
            <LabledInput
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Type customer name"
              label={"Name"}
            />

            <CustomSelect
              label="Gender"
              placeholder="Select Gender"
              value={gender}
              onChange={(e) => setGender(e.target.value)}
              options={["Male", "Female"]}
              required={true}
              inline
              showReset
            />
            <LabledInput
              type="date"
              value={dob}
              onChange={(e) => setDob(e.target.value)}
              placeholder="Date of Birth (optional)"
              label={"DOB"}
            />

            <div className="flex justify-center py-8">
              <button
                className="bg-[#FBB034] text-sm font-bold px-5 py-2 rounded-md"
                onClick={handleCreateUser}
                disabled={!name || !gender}
              >
                Create User
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ADDRESS LIST */}
      {customer && addresses.length > 0 && !isManualAddress && (
        <div className="flex flex-col gap-2">
          <label className="text-white text-sm">Saved Addresses</label>
          <select
            value={selectedAddressIndex}
            onChange={handleAddressSelect}
            className="bg-[#0c0b0b] text-white border border-[#E0E0E0] px-3 py-2 rounded"
          >
            <option value="">Select address</option>
            {addresses.map((addr, index) => (
              addr ? (
                <option key={index} value={index}>
                  {addr.flatNo}, {addr.apartment}
                </option>
              ) : null
            ))}
          </select>

          <button
            onClick={() => setIsManualAddress(true)}
            className="text-sm text-blue-400 underline text-left"
          >
            Enter address manually
          </button>
        </div>
      )}

      {/* MANUAL ADDRESS */}
      {(isManualAddress || addresses.length === 0) && customer && (
        <>
          <LabledInput
            value={flatNo}
            label="Flat No."
            onChange={(e) => setFlatNo(e.target.value)}
            placeholder={"Enter Flat/House Number"}
          />
          <ApartmentSearch
            onPlaceSelected={(place) => {
              handleApartmentSelected(place);
            }}
            showSaveButton={
              selectedApartment.length > 0
              && isServicable
            }
            onSave={() => handleUpdateAddress()}
          />
        </>
      )}

      {selectedApartment.length > 0 ? (
        empLoading ? (
          <p className="text-white">Checking availability...</p>
        ) : null
      ) : null}

      {/* LOCATION NOT SERVICABLE */}
      {isLocationChecked && !isServicable && (
        <p className="text-red-500">
          We are currently unavailable for this apartment.
        </p>
      )}

      {isServicable && (
        <>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
            <div className="flex items-center gap-3">
              <label className="text-white text-sm">
                Book without slot validation:
              </label>
              <button
                onClick={handleToggleSkip}
                className={`px-3 py-1 rounded ${skipSlotValidation
                    ? "bg-[#FBB034] text-black"
                    : "bg-[#0c0b0b] text-white border border-[#E0E0E0]"
                  }`}
              >
                {skipSlotValidation ? "ON" : "OFF"}
              </button>
            </div>
            <p className="text-gray-400 text-sm">
              When ON, admin must pick an employee manually and booking will be
              created without slot validation/assignment.
            </p>
          </div>
          <CustomSelect
            label="Gender"
            placeholder="Select Gender"
            value={customer.gender || ""}
            onChange={handleGenderChange}
            options={["Male", "Female"]}
            required={false}
            inline
            showReset
          />

          <LabledInput
            value={serviceSearchTerm}
            onChange={(e) => setServiceSearchTerm(e.target.value)}
            placeholder="Search services..."
            label={"Services"}
          />

          {filteredServices.length > 0 && serviceSearchTerm.length > 0 && (
            <div className="flex flex-wrap gap-4">
              {filteredServices.map((service) => (
                <button
                  key={service.id}
                  onClick={() => handleServiceSelect(service)}
                  className={`px-4 py-2 rounded ${selectedServices.some((s) => s.id === service.id)
                      ? "bg-[#FBB034] text-black"
                      : "bg-[#0c0b0b] text-white border border-[#E0E0E0]"
                    }`}
                >
                  {service.name} (₹{service.price})
                </button>
              ))}
            </div>
          )}
          {selectedServices.length > 0 && (
            <div className="mt-4">
              <h3 className="text-white mb-2">Selected Services:</h3>
              <div className="flex flex-wrap gap-2">
                {selectedServices.map((service) => (
                  <div
                    key={service.id}
                    className="flex items-center px-2 py-1 bg-[#FBB034] text-black rounded"
                  >
                    {service.name} (x{service.count})
                    <button
                      onClick={() => handleServiceRemove(service.id)}
                      className="ml-2 text-red-600"
                    >
                      ❌
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {selectedServices.length > 0 && (
            <div className="flex space-x-2 overflow-x-auto py-2">
              {generateNext7Days().map((day) => (
                <button
                  key={day.value}
                  onClick={() => handleDateSelect(day.value)}
                  className={`px-3 py-2 rounded ${date === day.value
                      ? "bg-blue-600 text-white"
                      : "bg-gray-800 text-gray-200"
                    }`}
                >
                  {day.label}
                </button>
              ))}
            </div>
          )}

          {/* If skipSlotValidation is ON we show employee picker (required) */}
          {
            // NOT skipping: show slots
            date ? (
              slotsLoading ? (
                <p className="text-white">Fetching slots...</p>
              ) : filteredSlots.length > 0 ? (
                <div className="flex flex-wrap gap-2 mt-4">
                  {filteredSlots.map((slot) => (
                    <div
                      key={slot}
                      onClick={() => setSelectedSlot(slot)}
                      className={`px-3 py-2 rounded cursor-pointer transition-colors
                            ${selectedSlot === slot
                          ? "bg-blue-600 text-white"
                          : "bg-green-600 text-white hover:bg-green-700"
                        }
                          `}
                    >
                      {slot}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-red-500">No available slots</p>
              )
            ) : null
          }
          {skipSlotValidation && (
            <div className="flex flex-col gap-4 mt-4">
              <CustomSelect
                label="Select Employee"
                placeholder="Select Employee"
                value={selectedEmployeeId}
                onChange={(e) => setSelectedEmployeeId(e.target.value)}
                options={allEmployees}
                required={true}
                inline
                showReset
                optionLabelKey="fullName"
                optionValueKey="id"
              />
              <LabledInput
                type="time"
                value={selectedSlot}
                onChange={(e) => setSelectedSlot(e.target.value)}
                label="Manual Time Slot (HH:mm)"
                placeholder="Select time"
              />
            </div>
          )}

          <LabledInput
            specialLabel={"₹"}
            value={calculateTotal()}
            disabled
            label={"Amount"}
          />

          <LabledInput
            specialLabel={"₹"}
            type="number"
            value={codCharges}
            onChange={(e) => setCodCharges(e.target.value)}
            label={"Handling Fee (Optional)"}
            placeholder={"Enter COD Charges"}
          />

          {prerequisiteCheckEnabled && applicableChecklistItems.length > 0 && (
            <div className="flex flex-col space-y-2 py-2">
              <p className="text-sm font-semibold">
                Please confirm with the customer:
              </p>
              {applicableChecklistItems.map((item) => (
                <div key={item.id} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id={`prerequisite-${item.id}`}
                    checked={acceptedChecklistItemIds.includes(item.id)}
                    onChange={(e) =>
                      setAcceptedChecklistItemIds((prev) =>
                        e.target.checked
                          ? [...prev, item.id]
                          : prev.filter((id) => id !== item.id)
                      )
                    }
                    className="w-5 h-5 cursor-pointer"
                  />
                  <label htmlFor={`prerequisite-${item.id}`}>
                    {item.title}
                  </label>
                </div>
              ))}
            </div>
          )}

          {(selectedSlot || skipSlotValidation) && (
            <div className="flex justify-center py-8">
              <button
                className="bg-[#FBB034] text-sm font-bold px-5 py-2 rounded-md disabled:opacity-50"
                onClick={bookingLoading ? null : handleSubmitBooking}
                disabled={
                  bookingLoading ||
                  (prerequisiteCheckEnabled &&
                    applicableChecklistItems.some(
                      (item) => !acceptedChecklistItemIds.includes(item.id)
                    ))
                }
              >
                {bookingLoading ? "Booking..." : "Create Booking"}
              </button>
            </div>
          )}
          {bookingError && <p className="text-red-500 mt-2">{bookingError}</p>}
        </>
      )}

      {bookingError && <p className="text-red-500">{bookingError}</p>}
    </div>
  );
};

export default CreateBooking;
